/**
 * Chapter notes/quiz (student), chapter admin and ingestion admin API client.
 * Wired into `api` in api.ts (api.chapters, api.adminChapters).
 */

type RequestFn = <T>(path: string, options?: RequestInit & { _silentPremium?: boolean }, token?: string | null) => Promise<T>;

export type SourceRef = {
  course_module_id: string;
  page: number;
  page_label?: string;
  label: string;
};

/** Server-computed access. The UI only renders it. */
export type AccessObject = {
  state: "open" | "preview" | "locked";
  reason: null | "course_premium" | "resource_restricted" | "plan_scope" | "login_required";
};

/** Slides and topics follow notes.schema.json. Fields are optional here because the renderer validates defensively. */
export type Slide = { id: string; type: string; title?: string; continued?: boolean; source_ref?: SourceRef } & Record<string, unknown>;
export type NotesTopic = { id: string; title: string; order_index?: number; slides: Slide[] };
export type ChapterNotes = { schema_version: number; chapter_id: string; version: number; title: string; topics: NotesTopic[] };
export type ChapterNotesResponse = ChapterNotes & { source_access: Record<string, AccessObject> };

/** `assets/…` paths of the image figures in a chapter's notes. */
export function collectImageSrcs(notes: ChapterNotes): string[] {
  const out = new Set<string>();
  for (const t of notes.topics ?? []) {
    for (const sl of t.slides ?? []) {
      const fig = (sl as { figure?: { kind?: string; src?: unknown } }).figure;
      if (sl.type === "figure" && fig?.kind === "image" && typeof fig.src === "string") out.add(fig.src);
    }
  }
  return [...out];
}

export type ChapterListItem = {
  id: string;
  order: number;
  title: string;
  version: number;
  is_preview: boolean;
  notes: { access: AccessObject; size_kb: number };
  quiz: { access: AccessObject; counts: Record<string, number> };
  offline_allowed: boolean;
  progress?: {
    notes_percent: number | null;
    last_position: { topic_id: string; slide_id: string } | null;
    quiz_best: Record<string, number | null>;
  };
};

export type ChapterDetail = {
  id: string;
  course_id: string;
  title: string;
  version: number;
  is_preview: boolean;
  topics: { id: string; title: string }[];
  notes: { access: AccessObject };
  quiz: { access: AccessObject };
  offline_allowed: boolean;
};

export type QuizOption = { id: string; text: string };

/** Per quiz.schema.json */
export type ChapterQuestionItem = {
  id: string;
  topic_id: string;
  difficulty: 1 | 2 | 3;
  type: "mcq" | "true_false";
  question: string;
  options: QuizOption[];
  answer: string;
  explanation: string;
  source_ref?: SourceRef;
  origin: "generated";
};

export type ChapterQuiz = {
  schema_version: number;
  chapter_id: string;
  version: number;
  questions: ChapterQuestionItem[];
};
export type ChapterQuizResponse = ChapterQuiz & { source_access: Record<string, AccessObject> };

export type TopicStat = { topic_id: string; title: string; total: number; correct: number; weak: boolean };

export type AttemptPayload = {
  attempt_id: string;
  chapter_id: string;
  chapter_version: number;
  difficulty: 1 | 2 | 3 | "mixed";
  mode: "practice";
  started_at: string;
  finished_at: string;
  answers: { question_id: string; answer: string }[];
};

export type ChapterQuizResult = {
  attempt_id: string;
  score_percentage: number;
  total_questions: number;
  correct_count: number;
  results: { question_id: string; answer: string; correct: boolean; topic_id: string }[];
  topics: TopicStat[];
  weak_topics: TopicStat[];
};

export type OfflineLease = {
  lease_id: string;
  issued_at: string;
  expires_at: string;
  server_time: string;
  signature: string;
};

export type ChapterOfflineBundle = {
  chapter_id: string;
  course_id: string;
  title: string;
  version: number;
  notes: ChapterNotes;
  quiz: ChapterQuiz;
  lease: OfflineLease;
};

export type NotesPosition = { topic_id: string; slide_id: string; chapter_version: number; client_updated_at: string };

export type StudyProgress = {
  courses: { course_id: string; slug: string; name: string; code: string; total: number; done: number }[];
  last_read: {
    chapter_id: string;
    chapter_title: string;
    course_id: string;
    course_slug: string;
    topic_id: string;
    slide_id: string;
    updated_at: string;
  } | null;
};

export type AttemptHistoryItem = { attempt_id: string; created_at: string | null; difficulty: string; score_percentage: number; total_questions: number };

export const chaptersStudentApi = (request: RequestFn) => ({
  progress: (token: string) => request<StudyProgress>("/study/progress", {}, token),
  attempts: (token: string, chapterId: string) =>
    request<{ items: AttemptHistoryItem[] }>(`/study/chapters/${encodeURIComponent(chapterId)}/attempts`, {}, token),
  list: (token: string, courseId: string) =>
    request<{ course_id: string; items: ChapterListItem[] }>(`/study/courses/${encodeURIComponent(courseId)}/chapters`, {}, token),
  detail: (token: string, chapterId: string) =>
    request<ChapterDetail>(`/study/chapters/${encodeURIComponent(chapterId)}`, {}, token),
  notes: (token: string, chapterId: string) =>
    request<ChapterNotesResponse>(`/study/chapters/${encodeURIComponent(chapterId)}/notes`, {}, token),
  quiz: (token: string, chapterId: string) =>
    request<ChapterQuizResponse>(`/study/chapters/${encodeURIComponent(chapterId)}/quiz`, {}, token),
  submitAttempt: (token: string, attempt: AttemptPayload) =>
    request<ChapterQuizResult>(
      `/study/chapters/${encodeURIComponent(attempt.chapter_id)}/quiz/attempts`,
      { method: "POST", body: JSON.stringify(attempt) },
      token,
    ),
  getPosition: (token: string, chapterId: string) =>
    request<{ position: NotesPosition | null }>(`/study/chapters/${encodeURIComponent(chapterId)}/position`, {}, token),
  putPosition: (
    token: string,
    chapterId: string,
    body: { chapter_version: number; topic_id: string; slide_id: string; client_updated_at: string },
  ) =>
    request<NotesPosition>(
      `/study/chapters/${encodeURIComponent(chapterId)}/position`,
      { method: "PUT", body: JSON.stringify(body) },
      token,
    ),
  versions: (token: string, ids: string[]) =>
    request<{ versions: Record<string, number> }>(
      `/study/chapters/versions?ids=${ids.map(encodeURIComponent).join(",")}`,
      { _silentPremium: true },
      token,
    ),
  lease: (token: string) => request<OfflineLease>("/study/offline/lease", { method: "POST", _silentPremium: true }, token),
  offlineBundle: (token: string, chapterId: string) =>
    request<ChapterOfflineBundle>(`/study/chapters/${encodeURIComponent(chapterId)}/offline`, {}, token),
});

// ─── Admin ───────────────────────────────────────────────────────────────────

export type AdminChapterItem = {
  id: string;
  order: number;
  title: string;
  status: string;
  live_version: number;
  latest_draft_version: number | null;
  version_count: number;
  is_preview: boolean;
  archived: boolean;
  is_public_sample: boolean;
  updated_at: string | null;
  topic_count: number;
  figure_count: number;
  quiz_count: number;
  /** A live chapter with a newer draft: the draft's changes are not shown to students yet. */
  draft_changes_pending: boolean;
  appended_topics: number;
};

export type CourseAccessSettings = {
  modules: "per_file" | "free" | "premium";
  notes: "free" | "premium" | "preview_only";
  quizzes: "free" | "premium" | "preview_only";
  exams: "per_file" | "free" | "premium";
};

export type AdminChapterList = {
  course: { id: string; access: { premium: boolean; settings: CourseAccessSettings } };
  /** Published files per kind and how many of them are premium (for "Set per file"). */
  files: Record<"modules" | "exams", { total: number; premium: number }>;
  items: AdminChapterItem[];
};

export type AdminChapterVersion = {
  added_figures: number;
  version: number;
  title: string;
  status: string;
  notes_size: number;
  source: string;
  created_at: string | null;
  published_at: string | null;
};

export type AdminVersionPreview = AdminChapterVersion & {
  chapter_id: string;
  notes: ChapterNotes;
  questions: ChapterQuestionItem[];
};

export type IngestionState = {
  enabled: boolean;
  enabled_until: string | null;
  seconds_left: number;
  auto_off_minutes: number;
  min_questions_per_difficulty: number;
  ip_allowlist: string[];
  has_key: boolean;
  key_prefix: string | null;
  key_created_at: string | null;
};

export type IngestionAuditItem = {
  id: string;
  created_at: string | null;
  ip: string | null;
  outcome: string;
  dry_run: boolean;
  chapter_count: number;
  summary: Record<string, unknown> | null;
};

export type IngestionImportItem = {
  id: string;
  chapter_id: string;
  version: number;
  action: string;
  needs_review: boolean;
  report: { warnings?: string[]; added_figures?: string[]; question_counts?: Record<string, number> | null };
  created_at: string | null;
};

export const chaptersAdminApi = (request: RequestFn) => ({
  archive: (token: string, chapterId: string) =>
    request<{ archived: boolean }>(`/admin/chapters/${encodeURIComponent(chapterId)}/archive`, { method: "POST" }, token),
  unarchive: (token: string, chapterId: string) =>
    request<{ archived: boolean }>(`/admin/chapters/${encodeURIComponent(chapterId)}/unarchive`, { method: "POST" }, token),
  /** chapterId null clears the public sample. */
  setPublicSample: (token: string, chapterId: string | null) =>
    request<{ chapter_id: string | null }>("/admin/public-sample", { method: "PUT", body: JSON.stringify({ chapter_id: chapterId }) }, token),
  uploadAsset: (token: string, chapterId: string, version: number, file: File) => {
    const form = new FormData();
    form.append("file", file);
    form.append("version", String(version));
    return request<{ src: string; bytes: number }>(`/admin/chapters/${encodeURIComponent(chapterId)}/assets`, { method: "POST", body: form }, token);
  },
  listAssets: (token: string, chapterId: string) =>
    request<{ items: string[] }>(`/admin/chapters/${encodeURIComponent(chapterId)}/assets`, {}, token),
  reorder: (token: string, courseId: string, ids: string[]) =>
    request<{ ids: string[] }>(
      `/admin/courses/${encodeURIComponent(courseId)}/chapters/order`,
      { method: "PUT", body: JSON.stringify({ ids }) },
      token,
    ),
  imports: (token: string, needsReview?: boolean) =>
    request<{ items: IngestionImportItem[] }>(
      `/admin/ingestion/imports${needsReview === undefined ? "" : `?needs_review=${needsReview}`}`,
      {},
      token,
    ),
  list: (token: string, courseId: string) =>
    request<AdminChapterList>(`/admin/courses/${encodeURIComponent(courseId)}/chapters`, {}, token),
  setAccess: (token: string, courseId: string, body: Partial<CourseAccessSettings>) =>
    request<CourseAccessSettings>(
      `/admin/courses/${encodeURIComponent(courseId)}/chapter-access`,
      { method: "PATCH", body: JSON.stringify(body) },
      token,
    ),
  update: (token: string, chapterId: string, body: { is_preview?: boolean; order?: number; title?: string }) =>
    request<{ id: string }>(
      `/admin/chapters/${encodeURIComponent(chapterId)}`,
      { method: "PATCH", body: JSON.stringify(body) },
      token,
    ),
  versions: (token: string, chapterId: string) =>
    request<{ chapter_id: string; live_version: number; status: string; items: AdminChapterVersion[] }>(
      `/admin/chapters/${encodeURIComponent(chapterId)}/versions`,
      {},
      token,
    ),
  preview: (token: string, chapterId: string, version: number) =>
    request<AdminVersionPreview>(`/admin/chapters/${encodeURIComponent(chapterId)}/versions/${version}`, {}, token),
  publish: (token: string, chapterId: string, version: number, confirm = false) =>
    request<{ changed: boolean }>(
      `/admin/chapters/${encodeURIComponent(chapterId)}/versions/${version}/publish${confirm ? "?confirm=true" : ""}`,
      { method: "POST" },
      token,
    ),
  /** Read-only: what publishing an ingested version over a different live version would replace. */
  publishCheck: (token: string, chapterId: string, version: number) =>
    request<{ warnings: string[]; confirm_required: boolean }>(
      `/admin/chapters/${encodeURIComponent(chapterId)}/versions/${version}/publish-check`,
      {},
      token,
    ),
  redraft: (token: string, chapterId: string, version: number) =>
    request<{ new_version: number }>(
      `/admin/chapters/${encodeURIComponent(chapterId)}/versions/${version}/redraft`,
      { method: "POST" },
      token,
    ),
  unpublish: (token: string, chapterId: string) =>
    request<{ changed: boolean }>(`/admin/chapters/${encodeURIComponent(chapterId)}/unpublish`, { method: "POST" }, token),
  ingestion: {
    get: (token: string) => request<IngestionState>("/admin/ingestion", {}, token),
    config: (
      token: string,
      body: { auto_off_minutes?: number; min_questions_per_difficulty?: number; ip_allowlist?: string[] },
    ) => request<IngestionState>("/admin/ingestion/config", { method: "PUT", body: JSON.stringify(body) }, token),
    enable: (token: string, minutes?: number) =>
      request<IngestionState>("/admin/ingestion/enable", { method: "POST", body: JSON.stringify({ minutes }) }, token),
    disable: (token: string) => request<IngestionState>("/admin/ingestion/disable", { method: "POST" }, token),
    newKey: (token: string) => request<IngestionState & { key: string }>("/admin/ingestion/key", { method: "POST" }, token),
    audit: (token: string) => request<{ items: IngestionAuditItem[] }>("/admin/ingestion/audit", {}, token),
  },
});
